import * as Sentry from "@sentry/nextjs";
import { ENV, RUNTIME } from "@/constants";
import { EnvironmentEnum, ErrorChannelEnum, RuntimeEnum } from "@/enums";
import type { ErrorContext, ReportPayload } from "@/types";
import { getRequestIdFromDom } from "@/utils/request-id";
import { toSentryLevel } from "./levels";
import { markNormalized, markReported } from "./marks";
import { normalizeError } from "./normalize";

function toChannel(c?: ErrorContext["channel"]) {
  return c ?? ErrorChannelEnum.UNKNOWN;
}

function buildPayload(err: unknown, ctx?: ErrorContext): ReportPayload {
  const n = normalizeError(err);

  const withClientRequestId = (): string | undefined => {
    if (RUNTIME !== RuntimeEnum.CLIENT) return ctx?.requestId;
    return getRequestIdFromDom() ?? ctx?.requestId;
  };

  return {
    timestamp: new Date().toISOString(),
    env: ENV,
    context: {
      runtime: ctx?.runtime ?? n.runtime,
      channel: toChannel(ctx?.channel),
      origin: ctx?.origin ?? n.origin,
      requestId: withClientRequestId(),
      userId: ctx?.userId,
      locale: ctx?.locale,
      url: ctx?.url,
      method: ctx?.method,
      status: ctx?.status ?? n.status,
      tags: ctx?.tags,
      meta: { ...n.details, ...ctx?.meta },
    },
    error: n,
  };
}

// Forwards a normalized payload to Sentry; SDK calls no-op when init was skipped (missing DSN)
async function reportToSentry(original: unknown, p: ReportPayload) {
  const level = toSentryLevel(p.error.severity);
  let fingerprint: string[] | undefined;
  const overrideFp = p.context.meta?.fingerprint as unknown;
  if (typeof overrideFp === "string") fingerprint = [overrideFp];
  else if (
    Array.isArray(overrideFp) &&
    overrideFp.every((x) => typeof x === "string")
  )
    fingerprint = overrideFp as string[];

  const setScope = (scope: Sentry.Scope) => {
    scope.setLevel(level);
    scope.setTags({
      ...(p.context.tags ?? {}),
      channel: p.context.channel,
      origin: p.context.origin,
      ...(p.context.requestId ? { requestId: p.context.requestId } : {}),
      ...(p.context.userId ? { userId: p.context.userId } : {}),
      ...(p.context.locale ? { locale: p.context.locale } : {}),
    });
    scope.setExtras({
      code: p.error.code,
      status: p.context.status,
      ...(p.context.meta ?? {}),
      ...(p.error.details ?? {}),
    });
    if (fingerprint) scope.setFingerprint(fingerprint);
  };

  try {
    if (original instanceof Error) {
      markReported(original);
      markNormalized(original);
      Sentry.withScope((scope) => {
        setScope(scope);
        Sentry.captureException(original);
      });
    } else {
      Sentry.withScope((scope) => {
        setScope(scope);
        Sentry.captureMessage(p.error.message, level);
      });
    }
  } catch {}
}

export async function reportError(err: unknown, ctx?: ErrorContext) {
  const p = buildPayload(err, ctx);

  if (ENV !== EnvironmentEnum.PRODUCTION) {
    const log = RUNTIME === RuntimeEnum.CLIENT ? console.debug : console.error;
    log(p);
  } else {
    console.error({
      timestamp: p.timestamp,
      env: p.env,
      context: {
        runtime: p.context.runtime,
        channel: p.context.channel,
        origin: p.context.origin,
        requestId: p.context.requestId,
        userId: p.context.userId,
        locale: p.context.locale,
        url: p.context.url,
        method: p.context.method,
        status: p.context.status,
        tags: p.context.tags,
      },
      error: {
        name: p.error.name,
        code: p.error.code,
        message: p.error.message,
        status: p.error.status,
      },
    });
  }
  await reportToSentry(err, p);
}
